/** @jsxImportSource @emotion/react */
import { StyleSheet, View } from "@react-pdf/renderer";
import { useMemo } from "react";
import { getFilledArrayOrDefault, isFilledArray } from "../../../../../utils";
import { PDFCheckFruitTables } from "./PDFCheckFruitTables";
import PDFDryMatterMinMaxAvgOverview from "./PDFDryMatterMinMaxAvgOverview";
import { PDFImages } from "./PDFImages";
import { PDFDocument, PDFPage } from "./PDFLayout";
import PDFPalletScoringOverview, { calculateStatusPercentages } from "./PDFPalletScoringOverview";
import { PDFPallets } from "./PDFPallets";
import { PDFDefectGroups, PDFLayerMeta, PDFSingleColumnMeta } from "./components";
import { filterChecksByReportView } from "./utils/reportView";

const styles = StyleSheet.create({
    row: {
        flexDirection: "row",
        justifyContent: "space-between",
        flexGrow: 1,
        marginTop: 20
    },
    cell1: {
        width: "35%", // Adjust based on your layout
    },
    cell2: {
        width: "63%", // Adjust based on your layout
    },
});

export const LayerMetaCheckSummaryTemplate = ({ layer_fields, general_status = undefined, pdf_config, children }) => {
    return (
        <View style={styles.row} wrap={false}>
            <View style={styles.cell1}>
                <PDFLayerMeta fields={layer_fields} status={general_status} categories={pdf_config.categories} />
            </View>

            <View style={styles.cell2}>
                {children}
            </View>
        </View>
    );
};

export const TemplateGeneralIntakeReport = ({ onRender, pdf_config, general_status, layer_config, layer, data, config, tenantDefectDefinitions, defect_groups, check_types, viewType }) => {
    // ! Warning: do not use any hooks that rely on the redux or api calls beyond this point. You will break the pdf rendering
    const { children_table_fields, children_title, children_check_summary_meta_fields, check_meta_fields } = pdf_config ?? {} as any;
    const logo_path = data?.logo_base64 || ""; // Use preloaded base64 logo

    const layer_fields = getFilledArrayOrDefault(layer_config.meta_display).map((i) => i.set_value({
        config,
        layer,
        check: layer.latest_check, // * To be used in check tables and layer overview
        fruit: null, // * Fruit list is not available on overview screen
        children: { count: data.children?.length || 0, results: getFilledArrayOrDefault(data.children, []) }, // * children is only available for layer index your are viewing
        form_options: config.form_options // * used to translate form values to labels
    }));

    const children_layer_fields = data.children.map((child) => {
        const fieldsForChild = config.applyDisplayFilterLayerFields(children_check_summary_meta_fields, {
            layer_type: child.type,
            fruit_type: child.fruit_type,
            layer: child,
            check: child.latest_check,
        });

        return fieldsForChild.map((i) => i.clone().set_value({
            config,
            layer: child,
            check: child.latest_check, // * To be used in check tables and layer overview
            fruit: null,
            children: null,
            form_options: config.form_options // * used to translate form values to labels
        }));
    });

    const arrival_check_fields = getFilledArrayOrDefault(layer_config.show_arrival_check?.fields).map((i) => i.clone().set_value({
        config,
        layer,
        check: data?.arrival_check || {}, // * To be used in check tables and layer overview
        fruit: null,
        children: null,
        form_options: config.form_options // * used to translate form values to labels
    }));

    // * The border of the pallets scoring overview should always be the color of the manual flag
    const overruled_status = layer?.[pdf_config.manual_flag_field];
    const percentages = calculateStatusPercentages(data.children, check_types);

    const hasArrivalCheckFields = useMemo(() => isFilledArray(arrival_check_fields), [arrival_check_fields]);
    const hasPalletsOverviewFields = useMemo(() => isFilledArray(children_table_fields), [children_table_fields]);

    const childFieldsByLayerId = useMemo(() => {
        return data.children.reduce((acc, child, i) => ({ ...acc, [child.id]: children_layer_fields[i] }), {});
    }, [data.children, children_layer_fields]);

    const filteredChecks = useMemo(() => {
        return filterChecksByReportView(
            getFilledArrayOrDefault(data.checks),
            getFilledArrayOrDefault(data.children),
            viewType
        )
            .map((check) => ({ check, fields: childFieldsByLayerId[check.layer_id] || [] }))
            .map(({ check, fields }, index) => [
                <PDFCheckFruitTables
                    key={index + 0.1}
                    fields={fields}
                    header={check.table_data[0]}
                    rows={check.table_data[1]}
                    footer={check.table_data[2]}
                    tenantDefectDefinitions={tenantDefectDefinitions}
                    defect_groups={defect_groups}
                    check={check}
                    check_meta_fields={check_meta_fields}
                />,
                <PDFImages key={index + 0.2} images={check.all_images} />
            ]);
    }, [data?.children, data?.checks, childFieldsByLayerId, viewType]);

    return (
        <>
            <PDFDocument onRender={onRender} title={`${(pdf_config.title || "Intake report")} ${layer?.label}`}>
                <PDFPage logo_path={logo_path} header_title={pdf_config.title || "Intake report"}>
                    <LayerMetaCheckSummaryTemplate
                        general_status={general_status}
                        pdf_config={pdf_config}
                        layer_fields={layer_fields}
                    >
                        {defect_groups.find((item) => item.pdf_show_on_po_level) && (
                            <PDFDefectGroups
                                allDefects={tenantDefectDefinitions}
                                defectGroups={defect_groups.filter((item) => item.pdf_show_on_po_level)}
                                checks={data.checks}
                            />
                        )}

                        {pdf_config?.show_dry_matter && (
                            <PDFDryMatterMinMaxAvgOverview dryMatterMinMaxAvg={data.dryMatterMinMaxAvg} />
                        )}

                        <PDFPalletScoringOverview
                            status={overruled_status}
                            percentages={percentages}
                        />
                    </LayerMetaCheckSummaryTemplate>
                </PDFPage>

                {hasArrivalCheckFields && data.arrival_check && (
                    <PDFPage>
                        <PDFSingleColumnMeta fields={arrival_check_fields} title="Arrival" />
                    </PDFPage>
                )}

                {hasPalletsOverviewFields && (
                    <PDFPallets
                        fields={children_table_fields}
                        printChildren={data.children}
                        config={config}
                        children_title={children_title}
                    />
                )}

                {filteredChecks}
            </PDFDocument>
        </>
    );
};
